import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
    getProjects,
    getAbout,
    getSiteImages,
    getFileServeUrl,
    getFeaturedProjects,
} from "../../pages/g4/lib/g4Api";
import SEO from "../../components/g4/SEO";
import { FadeInSection } from "../../components/g4/FadeIn";
import { G4Navbar, G4Footer } from "../../components/g4/Chrome";

const API_BASE = "";

const FALLBACK_HERO =
    "https://static.prod-images.emergentagent.com/jobs/14d3d746-4feb-408b-902b-6715506dd42e/images/4a98714f6572afb5c1ec4aee78f6426204baafe89e7888a3e2b145b2791ea031.png";
const FALLBACK_STUDIO =
    "https://static.prod-images.emergentagent.com/jobs/14d3d746-4feb-408b-902b-6715506dd42e/images/331cbc18dd5a053fd964e957d57861abeabcb080e1576f45587ad22ef9d9d23e.png";

function resolveUrl(url) {
    if (!url) return "";
    if (url.startsWith("/api/")) return `${API_BASE}${url}`;
    return url;
}

function HeroSection({ heroImg }) {
    return (
        <section data-testid="hero-section" className="relative h-screen w-full overflow-hidden">
            <img
                src={heroImg}
                alt="Studio G4 Architecture"
                className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="hero-overlay absolute inset-0" />
            <div className="relative z-10 h-full flex flex-col justify-end pb-24 md:pb-32 max-w-7xl mx-auto px-6 md:px-12">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/80 mb-4 animate-fade-up stagger-1">
                    Architecture · Project Management · Construction
                </p>
                <h1 className="font-['Outfit'] text-4xl md:text-6xl lg:text-7xl tracking-tighter font-light text-white max-w-3xl leading-[1.05] animate-fade-up stagger-2">
                    Thoughtful Design. <br />Exceptional Spaces.
                </h1>
                <p className="text-base text-white/70 max-w-lg mt-6 leading-relaxed animate-fade-up stagger-3">
                    We create beautifully considered spaces that balance design ambition with everyday functionality, guiding each project from initial concept through to construction.
                </p>
                <div className="flex gap-4 mt-8 animate-fade-up stagger-4">
                    <a
                        href="#portfolio"
                        data-testid="hero-view-work-btn"
                        className="px-8 py-3 text-sm tracking-wide font-bold inline-flex items-center gap-2 transition-all duration-200 hover:opacity-80"
                        style={{ backgroundColor: "rgba(220,220,220,0.85)", color: "#650505" }}
                    >
                        View Our Work <ArrowRight size={16} />
                    </a>
                </div>
            </div>
        </section>
    );
}

function RichContent({ html, className = "" }) {
    if (!html) return null;
    const text = html.replace(/<[^>]*>/g, "").trim();
    if (!text) return null;
    if (html === text) return <p className={className}>{html}</p>;
    return (
        <div
            className={`rich-content ${className}`}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}

function AboutSection({ about, aboutImg }) {
    return (
        <section id="about" data-testid="about-section" className="py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <FadeInSection>
                        <h2 className="font-['Outfit'] text-3xl md:text-4xl tracking-tight font-normal text-[#0A0A0A] mb-8">
                            {about.headline || "Crafting Spaces That Inspire"}
                        </h2>
                        <RichContent
                            html={about.description}
                            className="text-base leading-relaxed text-[#525252] mb-6"
                        />
                    </FadeInSection>
                    <FadeInSection delay={0.2}>
                        <div className="relative">
                            <img
                                src={aboutImg}
                                alt="Studio G4 Interior"
                                className="w-full aspect-[3/4] object-cover"
                            />
                        </div>
                    </FadeInSection>
                </div>
            </div>
        </section>
    );
}

function PortfolioSection({ projects, portfolioImgs, orientations = {} }) {
    return (
        <section
            id="portfolio"
            data-testid="portfolio-section"
            className="py-24 md:py-32 bg-[#FAFAFA]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="mb-12">
                    <h2 className="font-['Outfit'] text-3xl md:text-4xl tracking-tight font-normal text-[#0A0A0A]">
                        Featured Projects
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[480px]">
                    {projects.map((project, i) => {
                        const orientation = orientations[project.id] || "landscape";
                        const isPortrait = orientation === "portrait";
                        const span = isPortrait ? "" : "md:col-span-2";
                        const imgSrc = project.cover_image
                            ? getFileServeUrl(project.cover_image)
                            : project.images && project.images.length > 0
                            ? getFileServeUrl(project.images[0])
                            : portfolioImgs[i % portfolioImgs.length];
                        return (
                            <Link
                                to={`/projects/${project.id}`}
                                key={project.id}
                                data-testid={`project-card-${i}`}
                                className={`${span} group relative overflow-hidden animate-fade-up cursor-pointer`}
                                style={{ animationDelay: `${i * 0.1}s` }}
                            >
                                <img
                                    src={imgSrc}
                                    alt={project.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                    <p className="text-xs tracking-[0.15em] uppercase text-white/70 mb-1">
                                        {project.category}
                                    </p>
                                    <h3 className="font-['Outfit'] text-xl text-white font-normal">
                                        {project.title}
                                    </h3>
                                    <p className="text-sm text-white/60 mt-1">{project.location}</p>
                                </div>
                            </Link>
                        );
                    })}
                </div>
                {projects.length === 0 && (
                    <p className="text-center text-sm text-[#525252] py-16">
                        No featured projects yet.
                    </p>
                )}
            </div>
        </section>
    );
}

function QuoteCTA() {
    return (
        <section id="quote" data-testid="quote-cta-section" className="py-24 md:py-32 bg-[#0A0A0A]">
            <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
                <FadeInSection>
                    <h2 className="font-['Outfit'] text-3xl md:text-4xl tracking-tight font-normal text-white mb-6 max-w-2xl mx-auto">
                        Have a Project in Mind?
                    </h2>
                    <p className="text-base text-white/60 max-w-lg mx-auto mb-10 leading-relaxed">
                        Send us a summary of your requirements and we will be in touch to discuss further. Typically within 24 hours.
                    </p>
                    <Link
                        to="/request-quote"
                        data-testid="cta-request-quote-btn"
                        className="inline-flex items-center gap-2 border border-white text-white px-10 py-3.5 text-sm tracking-wide font-medium hover:bg-white hover:text-[#0A0A0A] transition-all duration-200"
                    >
                        Get in Touch <ArrowRight size={16} />
                    </Link>
                </FadeInSection>
            </div>
        </section>
    );
}

function Footer() {
    return <G4Footer />;
}

export default function HomePage() {
    const [projects, setProjects] = useState([]);
    const [featuredIds, setFeaturedIds] = useState(null);
    const [featuredOrientations, setFeaturedOrientations] = useState({});
    const [about, setAbout] = useState({ headline: "", description: "", mission: "", vision: "" });
    const [siteImages, setSiteImages] = useState(null);

    useEffect(() => {
        getProjects().then(setProjects).catch(() => {});
        getFeaturedProjects()
            .then((d) => {
                setFeaturedIds(d.project_ids || []);
                setFeaturedOrientations(d.orientations || {});
            })
            .catch(() => setFeaturedIds([]));
        getAbout().then(setAbout).catch(() => {});
        getSiteImages()
            .then(setSiteImages)
            .catch(() => setSiteImages({}));
    }, []);

    if (!siteImages) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <p className="font-['Outfit'] text-lg text-[#0A0A0A]">Loading...</p>
            </div>
        );
    }

    // siteImages.hero + about are already fully resolved by g4Api.getSiteImages()
    // (bare UUID → /g4-media/<id>); portfolio_* / portal_bg are absolute URLs
    // passed through unchanged by resolveUrl.
    const heroImg = siteImages.hero || FALLBACK_HERO;
    const aboutImg = siteImages.about || FALLBACK_STUDIO;
    const portfolioImgs = [
        resolveUrl(siteImages.portfolio_1),
        resolveUrl(siteImages.portfolio_2),
        resolveUrl(siteImages.portfolio_3),
    ].filter(Boolean);

    const displayProjects =
        featuredIds && featuredIds.length > 0
            ? featuredIds.map((id) => projects.find((p) => p.id === id)).filter(Boolean)
            : projects;

    return (
        <div data-testid="home-page">
            <SEO path="/" />
            <G4Navbar />
            <HeroSection heroImg={heroImg} />            <AboutSection about={about} aboutImg={aboutImg} />
            <PortfolioSection
                projects={displayProjects}
                portfolioImgs={portfolioImgs.length > 0 ? portfolioImgs : [FALLBACK_HERO]}
                orientations={featuredOrientations}
            />
            <QuoteCTA />
            <Footer />
        </div>
    );
}
